import React from 'react';
import { MapWorkspaceOrientation } from './FmMapWorkspaceOrientation';
import { FmMapWorkspaceMap } from './FmMapWorkspaceMap';
import { actionDismissesMapPanel, selectionForMapFeature } from './FmMapWorkspaceSelection';
import {
  useMapWorkspaceFilters,
  type FmMapEmitFilter as EmitFilter,
} from './useMapWorkspaceFilters';
import { MapWorkspacePanelContent } from './FmMapWorkspacePanelRegistry';
import { useRegisteredMapWorkspacePanels } from './FmMapWorkspacePanelRegistry.hooks';
import { FmMapWorkspacePreviewPanelTools } from './FmMapWorkspacePreviewPanelTools';
import { validateMapWorkspaceDescriptor } from './FmMapWorkspaceRoot.validation';
import type {
  FmMapFilterValues as FilterValues,
  FmMapLayerId,
  FmMapPanelDescriptor,
  FmMapWorkspaceDescriptor,
  FmMapWorkspaceRootProps,
} from './FmMapWorkspaceRoot.types';
import { FmBanner } from './FmBanner';
import { FmButton } from './FmButton';
import { FmChip } from './FmChip';
import { FmInline, FmStack, FmText } from './FmComposition';
import { FmDrawer } from './FmDrawer';
import { FmMapObservationProvider } from './FmMapObservationState';
import { FmMapViewportProvider } from './FmMapViewportState';
import { FmResponsive } from './FmResponsive';
import type { FmDeviceTier } from './FmResponsive';
import FmRightInspector, { type FmRightInspectorEvent } from './FmRightInspector';
import { mapLayerCapability } from './FmMapLayerCapabilities';
import { MapWorkspaceDialogs } from './internal/MapWorkspaceDialogs';
import FmContextPanel from '@/shared/ui/FmContextPanel';
import FmMapTools from '@/shared/ui/FmMapTools';
import { FmContextPanelSlot } from '@/contexts/FmContextPanelContext';
import { useFmContextPanel } from '@/contexts/fmContextPanelInternals';
import './FmMapWorkspaceRoot.css';
import { fmMapOcclusion } from '@/shared/ui/mapOcclusion';

const WorkspaceNotice: React.FC<{ descriptor: FmMapWorkspaceDescriptor }> = ({
  descriptor,
}) => {
  const notice = descriptor.notice;
  if (!notice) return null;
  return (
    <FmBanner
      tone="info"
      title={notice.title}
      description={notice.description}
      action={
        <FmButton
          size="sm"
          variant="bordered"
          onClick={() =>
            descriptor.onEvent?.({ kind: 'action', actionId: notice.actionId })
          }
        >
          {notice.actionLabel}
        </FmButton>
      }
      data-testid={notice.testId}
    />
  );
};

const WorkspaceState: React.FC<{ descriptor: FmMapWorkspaceDescriptor }> = ({ descriptor }) =>
  descriptor.state.kind === 'ready' ? null : (
    <output className="fm-map-workspace__state" {...fmMapOcclusion('top', 'panel')}>
      <FmText>{descriptor.state.label}</FmText>
    </output>
  );

const OrientationAnswer: React.FC<{ descriptor: FmMapWorkspaceDescriptor }> = ({ descriptor }) => {
  const behavior = descriptor.story.stateBehavior[descriptor.state.kind];
  const answer = behavior.retainsServedAnswer ? descriptor.story.literalAnswer : behavior.answer;
  if (!descriptor.orientation) {
    return (
      <MapWorkspaceOrientation
        ariaLabel={descriptor.story.ownerQuestion}
        caption={descriptor.modeLabel}
        ownerQuestion={descriptor.story.ownerQuestion}
        literalAnswer={answer}
      />
    );
  }
  return (
    <MapWorkspaceOrientation
      ariaLabel={descriptor.orientation.ariaLabel}
      caption={descriptor.orientation.propertyLabel}
      ownerQuestion={descriptor.story.ownerQuestion}
      literalAnswer={answer}
    />
  );
};

const AppliedFilterChips: React.FC<{
  descriptor: FmMapWorkspaceDescriptor;
  emitFilter: EmitFilter;
  filterValues: FilterValues;
}> = ({ descriptor, emitFilter, filterValues }) => {
  const entries = Object.entries(filterValues).filter(([, values]) => values.length > 0);
  if (entries.length === 0) return null;
  return (
    <div
      className="fm-map-workspace__applied"
      aria-label={descriptor.copy.appliedFiltersAriaLabel}
      {...fmMapOcclusion('bottom', 'panel')}
    >
      <FmInline gap="xs" wrap>
        {entries.flatMap(([filterId, values]) =>
          values.map((value) => (
            <FmChip
              as="button"
              key={`${filterId}-${value}`}
              variant="info"
              onClick={() =>
                emitFilter(
                  filterId,
                  values.filter((item) => item !== value),
                  'undo',
                )
              }
            >
              {descriptor.filters
                .find((filter) => filter.id === filterId)
                ?.options.find((option) => option.value === value)?.label ?? value}{' '}
              ×
            </FmChip>
          )),
        )}
      </FmInline>
    </div>
  );
};

const PreviewPanelSurface: React.FC<{
  activePanel: FmMapPanelDescriptor | undefined;
  isPreview: boolean;
  onClose: () => void;
  panelContent: React.ReactNode;
  tier: FmDeviceTier;
}> = ({ activePanel, isPreview, onClose, panelContent, tier }) => {
  if (!isPreview) return null;
  if (tier === 'desktop') {
    if (!activePanel) return null;
    return (
      <aside
        className="fm-map-workspace__panel"
        aria-label={activePanel.title}
        data-testid="map-workspace-desktop-panel"
        {...fmMapOcclusion('left', 'panel')}
      >
        <FmStack gap="md">
          <FmText as="h2">{activePanel.title}</FmText>
          {panelContent}
        </FmStack>
      </aside>
    );
  }
  return (
    <FmDrawer
      open={Boolean(activePanel)}
      onClose={onClose}
      title={activePanel?.title}
      placement="bottom"
      theme="light"
      data-testid="map-workspace-mobile-sheet"
    >
      {panelContent}
    </FmDrawer>
  );
};

const WorkspaceInspection: React.FC<{
  descriptor: FmMapWorkspaceDescriptor;
  hidden?: boolean;
}> = ({ descriptor, hidden = false }) => {
  const onEvent = React.useCallback(
    (event: FmRightInspectorEvent) => {
      if (event.kind === 'action') {
        descriptor.onEvent?.({ kind: 'action', actionId: event.actionId });
        return;
      }
      descriptor.onEvent?.({
        kind: event.kind === 'close' ? 'inspection-close' : 'inspection-open-full',
      });
    },
    [descriptor],
  );
  return descriptor.inspection && !hidden ? (
    <FmRightInspector descriptor={descriptor.inspection} onEvent={onEvent} />
  ) : null;
};

function WorkspaceSurface({
  activePanel,
  activePanelId,
  collectionPanelOpen,
  dataTestId,
  descriptor,
  emitFilter,
  filterValues,
  filterPanelOpen,
  isPreview,
  panelContent,
  panelOpen,
  previewTier,
  propertyId,
  registeredPanels,
  selectFeature,
  setActivePanelId,
}: {
  activePanel: FmMapPanelDescriptor | undefined;
  activePanelId: string | null;
  collectionPanelOpen: boolean;
  dataTestId: string;
  descriptor: FmMapWorkspaceDescriptor;
  emitFilter: EmitFilter;
  filterValues: FilterValues;
  filterPanelOpen: boolean;
  isPreview: boolean;
  panelContent: React.ReactNode;
  /** A context panel is showing in the map lane, so the answer card moves past it. */
  panelOpen: boolean;
  previewTier: FmDeviceTier | undefined;
  propertyId: string;
  registeredPanels: ReturnType<typeof useRegisteredMapWorkspacePanels>;
  selectFeature: (
    featureId: string,
    expectedKind?: 'stand' | 'plot',
    featurePropertyId?: string,
  ) => void;
  setActivePanelId: (panelId: string | null) => void;
}) {
  const fitLabel = descriptor.panels.find((panel) => panel.kind === 'legend')?.semantics
    .fitMapLabel;
  return (
    <FmResponsive tier={previewTier}>
      {(tier) => (
        <div
          className="fm-map-workspace-root map-view"
          data-fm-map-workspace
          data-preview={String(isPreview)}
          data-testid={dataTestId}
          data-panel-open={String(panelOpen)}
          data-inspection-open={String(descriptor.inspection !== null && !collectionPanelOpen)}
          data-filter-panel-open={String(filterPanelOpen)}
          data-mode={descriptor.mode}
          data-state={descriptor.state.kind}
          data-viewport-revision={descriptor.viewport.revision}
          data-tier={tier}
          data-owner-question={descriptor.story.ownerQuestion}
          data-dominant-beat={descriptor.story.dominantBeat}
          data-transitions={descriptor.story.allowedTransitions.join(' ')}
          data-conversion-role={descriptor.story.conversionRole}
          data-mobile-order={descriptor.story.mobileOrder.join(' ')}
        >
          <WorkspaceInspection descriptor={descriptor} hidden={collectionPanelOpen} />
          <MapWorkspaceDialogs descriptor={descriptor} />
          {!isPreview ? (
            <>
              <FmContextPanelSlot panels={registeredPanels} mapToolsPlacement="local" />
              <FmMapTools
                fitLabel={fitLabel}
                hoverPeek
                actions={descriptor.actions.flatMap((action) =>
                  action.tool
                    ? [
                        {
                          id: action.id,
                          label: action.label,
                          tool: action.tool,
                          disabledReason: action.disabledReason,
                          onSelect: () =>
                            descriptor.onEvent?.({
                              kind: 'action',
                              actionId: action.id,
                            }),
                        },
                      ]
                    : [],
                )}
              />
              <FmContextPanel hoverPeek />
            </>
          ) : null}
          <div
            className="fm-map-workspace__summary"
            {...fmMapOcclusion('top', 'panel')}
            data-testid="map-workspace-summary"
          >
            <WorkspaceNotice descriptor={descriptor} />
            <OrientationAnswer descriptor={descriptor} />
            <WorkspaceState descriptor={descriptor} />
          </div>
          <FmMapWorkspaceMap
            descriptor={descriptor}
            filterValues={filterValues}
            isPreview={isPreview}
            propertyId={propertyId}
            selectFeature={selectFeature}
          />
          <FmMapWorkspacePreviewPanelTools
            activePanelId={activePanelId}
            descriptor={descriptor}
            isPreview={isPreview}
            setActivePanelId={setActivePanelId}
          />
          <AppliedFilterChips
            descriptor={descriptor}
            emitFilter={emitFilter}
            filterValues={filterValues}
          />
          <PreviewPanelSurface
            activePanel={activePanel}
            isPreview={isPreview}
            onClose={() => setActivePanelId(null)}
            panelContent={panelContent}
            tier={tier}
          />
        </div>
      )}
    </FmResponsive>
  );
}

/** Descriptor-only five-mode spatial workspace. The renderer owns all map chrome and interaction. */
export const FmMapWorkspaceRoot: React.FC<FmMapWorkspaceRootProps> = ({
  descriptor,
  preview = false,
  previewTier,
  'data-testid': dataTestId = 'map-view',
}) => {
  validateMapWorkspaceDescriptor(descriptor);
  const isPreview = preview || previewTier !== undefined;
  const workspaceDescriptor = descriptor;
  const {
    activePanel: contextActivePanelId,
    closePanel,
    isPanelOpen: isContextPanelOpen,
    openPanel,
  } = useFmContextPanel();
  const emitPanelEvent = React.useCallback(
    (event: Parameters<NonNullable<FmMapWorkspaceDescriptor['onEvent']>>[0]) => {
      if (event.kind === 'collection-select') {
        const panel = descriptor.panels.find((candidate) => candidate.id === event.panelId);
        if (!(panel?.kind === 'collection' && panel.retainOnSelect === true)) {
          closePanel();
        }
      } else if (event.kind === 'action' && actionDismissesMapPanel(descriptor, event.actionId)) {
        closePanel();
      }
      descriptor.onEvent?.(event);
    },
    [closePanel, descriptor],
  );
  const panelDescriptor = React.useMemo(
    () => ({ ...workspaceDescriptor, onEvent: emitPanelEvent }),
    [emitPanelEvent, workspaceDescriptor],
  );
  const toggleLayer = React.useCallback(
    (layerId: FmMapLayerId, visible: boolean) =>
      descriptor.onEvent?.({ kind: 'layer-change', layerId, visible }),
    [descriptor],
  );
  const selectBaseMap = React.useCallback(
    (baseMapId: FmMapWorkspaceDescriptor['activeBaseMapId']) =>
      descriptor.onEvent?.({ kind: 'basemap-change', baseMapId }),
    [descriptor],
  );
  const [activePanelId, setActivePanelId] = React.useState(descriptor.activePanelId);
  const appliedDescriptorPanelRef = React.useRef<string | null>(null);
  const { emitFilter, filterValues } = useMapWorkspaceFilters(workspaceDescriptor);
  React.useEffect(() => setActivePanelId(descriptor.activePanelId), [descriptor.activePanelId]);
  React.useEffect(() => {
    if (descriptor.activePanelId === null) {
      appliedDescriptorPanelRef.current = null;
      return;
    }
    if (appliedDescriptorPanelRef.current === descriptor.activePanelId) return;
    appliedDescriptorPanelRef.current = descriptor.activePanelId;
    openPanel(descriptor.activePanelId);
  }, [descriptor.activePanelId, openPanel]);
  const activePanel = workspaceDescriptor.panels.find((panel) => panel.id === activePanelId);
  const renderedPanelId = isPreview
    ? activePanelId
    : isContextPanelOpen
      ? contextActivePanelId
      : null;
  const collectionPanelOpen = workspaceDescriptor.panels.some(
    (panel) => panel.id === renderedPanelId && panel.kind === 'collection',
  );
  const filterPanelOpen = workspaceDescriptor.panels.some(
    (panel) => panel.id === renderedPanelId && panel.kind === 'filters',
  );
  const propertyId = workspaceDescriptor.propertyId ?? '';
  const { features, onEvent } = workspaceDescriptor;
  const selectFeature = React.useCallback(
    (featureId: string, expectedKind?: 'stand' | 'plot', featurePropertyId?: string): void => {
      onEvent?.({
        kind: 'selection-change',
        selection: selectionForMapFeature(
          featureId,
          features,
          propertyId,
          expectedKind,
          featurePropertyId,
        ),
      });
    },
    [features, onEvent, propertyId],
  );
  const panelContent = activePanel ? (
    <MapWorkspacePanelContent
      activeBaseMapId={workspaceDescriptor.activeBaseMapId}
      baseMaps={workspaceDescriptor.baseMaps}
      emitFilter={emitFilter}
      filterValues={filterValues}
      filters={workspaceDescriptor.filters}
      layers={workspaceDescriptor.layers}
      onEvent={emitPanelEvent}
      panel={activePanel}
      toggleLayer={toggleLayer}
      selectBaseMap={selectBaseMap}
    />
  ) : null;
  const registeredPanels = useRegisteredMapWorkspacePanels(
    panelDescriptor,
    emitFilter,
    filterValues,
    { toggleLayer, selectBaseMap },
  );
  const radarEnabled = workspaceDescriptor.layers.some(
    (layer) => mapLayerCapability(layer).control === 'radar-playback' && layer.visible,
  );
  return (
    <FmMapObservationProvider enabled={radarEnabled} snapshot={workspaceDescriptor.radar}>
      <FmMapViewportProvider>
        <WorkspaceSurface
          activePanel={activePanel}
          activePanelId={activePanelId}
          collectionPanelOpen={collectionPanelOpen}
          dataTestId={dataTestId}
          descriptor={workspaceDescriptor}
          emitFilter={emitFilter}
          filterValues={filterValues}
          filterPanelOpen={filterPanelOpen}
          isPreview={isPreview}
          panelContent={panelContent}
          panelOpen={isPreview ? Boolean(activePanel) : renderedPanelId !== null}
          previewTier={previewTier}
          propertyId={propertyId}
          registeredPanels={registeredPanels}
          selectFeature={selectFeature}
          setActivePanelId={setActivePanelId}
        />
      </FmMapViewportProvider>
    </FmMapObservationProvider>
  );
};

FmMapWorkspaceRoot.displayName = 'FmMapWorkspaceRoot';
export default FmMapWorkspaceRoot;
